import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Currency conversion rates - GBP is base currency
const CONVERSION_RATES = {
  USD: 1.27, // 1 GBP = 1.27 USD
  GBP: 1 // Base currency
};

// Pricing in GBP (base currency) - EXACTLY as specified
const BASE_PRICES = {
  monthly: 39,
  quarterly: 90,
  lifetime: 299
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('GBP');
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  // Default to GBP pricing as requested; respect saved user choice if present
  useEffect(() => {
    // Force default GBP pricing
    setCurrency('GBP');
    setIsAutoDetected(true);
  }, []);

  const switchCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('preferred_currency', newCurrency);
    setIsAutoDetected(false);
  };

  const formatPrice = (priceInUSD) => {
    const rate = CONVERSION_RATES[currency];
    const convertedPrice = Math.round(priceInUSD * rate);

    if (currency === 'GBP') {
      return `£${convertedPrice}`;
    }
    return `$${convertedPrice}`;
  };

  const getPrice = (planType) => {
    const basePrice = BASE_PRICES[planType];
    const rate = CONVERSION_RATES[currency];
    return Math.round(basePrice * rate);
  };

  const getCurrencySymbol = () => {
    return currency === 'GBP' ? '£' : '$';
  };

  const getSavingsText = (planType) => {
    if (planType === 'quarterly') {
      const monthlyPrice = getPrice('monthly');
      const quarterlyPrice = getPrice('quarterly');
      const savings = (monthlyPrice * 3) - quarterlyPrice;
      return `Save ${getCurrencySymbol()}${savings}`;
    }
    return 'Best Deal';
  };

  const getMonthlyBreakdown = (planType) => {
    if (planType === 'quarterly') {
      const quarterlyPrice = getPrice('quarterly');
      const perMonth = (quarterlyPrice / 3).toFixed(2);
      return `${getCurrencySymbol()}${perMonth}/month`;
    }
    return null;
  };

  const value = {
    currency,
    switchCurrency,
    formatPrice,
    getPrice,
    getCurrencySymbol,
    getSavingsText,
    getMonthlyBreakdown,
    isAutoDetected,
    availableCurrencies: ['USD', 'GBP']
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
